import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  radiosCercanas,
  eventosCercanos,
  locutoresCercanos,
  ubicacionesDetectables,
  opcionesDistancia,
  opcionesCategoria,
  reglasRadiosCercanas,
  statsRadiosCercanas,
  formatearNumero,
  getRadiosEnVivoCercanas,
  getRadiosTendenciaCercanas,
  type RadioCercana,
  type EventoCercano,
  type LocutorCercano,
} from '@/mocks/radiosCercanas';

function DistanciaBadge({ km }: { km: number }) {
  const color = km <= 2 ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
    km <= 5 ? 'bg-amber-100 text-amber-700 border-amber-200' :
    'bg-background-100 text-foreground-500 border-background-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${color}`}>
      <i className="ri-map-pin-line text-[8px]"></i>
      {km} km
    </span>
  );
}

function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/90 text-white text-[9px] font-bold">
      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
      EN VIVO
    </span>
  );
}

function TendenciaBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/90 text-white text-[9px] font-bold">
      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
      TENDENCIA
    </span>
  );
}

function RadioCercanaCard({ radio, size }: { radio: RadioCercana; size?: 'featured' | 'compact' }) {
  const isFeatured = size === 'featured';
  const cat = opcionesCategoria.find(c => c.id === radio.categoria);

  return (
    <Link
      to="/radios-cercanas"
      className={`group block rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-emerald-300/60 transition-all duration-300`}
    >
      <div className={`relative ${isFeatured ? 'h-44' : 'h-32'} overflow-hidden`}>
        <img
          src={radio.imagenUrl}
          alt={radio.nombre}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/95 via-background-50/40 to-transparent"></div>

        <div className="absolute top-3 left-3 flex items-center gap-2">
          {radio.estaEnVivo && <LiveBadge />}
          {radio.enTendencia && <TendenciaBadge />}
          <DistanciaBadge km={radio.distancia} />
        </div>

        <div className="absolute top-3 right-3">
          <img src={radio.logoUrl} alt="" className="w-9 h-9 rounded-xl object-cover border-2 border-white/80 shadow-sm" />
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h4 className="text-sm font-bold text-foreground-950 line-clamp-1">{radio.nombre}</h4>
          {cat && (
            <span className="shrink-0 w-6 h-6 rounded-full bg-background-100 flex items-center justify-center">
              <i className={`${cat.icono} text-[10px] text-foreground-400`}></i>
            </span>
          )}
        </div>
        <p className="text-[10px] text-foreground-500 mb-1.5">{radio.frecuencia} · {radio.ciudad}</p>

        <div className="flex items-center gap-3 text-[9px] text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-headphone-line text-[8px]"></i>
            {formatearNumero(radio.oyentes)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-[8px] text-amber-500"></i>
            {radio.rating}
          </span>
          <span className="text-foreground-300">|</span>
          <span className="text-foreground-500 font-medium truncate">{radio.locutorPrincipal}</span>
        </div>

        {isFeatured && (
          <div className="flex flex-wrap gap-1 mt-3">
            {radio.tags.slice(0, 3).map(tag => (
              <span key={tag} className="px-1.5 py-0.5 rounded-md bg-background-100 text-[8px] text-foreground-500 border border-background-200/50">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

function EventoCercanoCard({ evento }: { evento: EventoCercano }) {
  const fecha = new Date(evento.fecha);
  const fechaStr = fecha.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <Link
      to="/radios-cercanas"
      className="group block rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-violet-300/60 transition-all duration-300"
    >
      <div className="relative h-32 overflow-hidden">
        <img
          src={evento.imagenUrl}
          alt={evento.titulo}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/95 via-background-50/30 to-transparent"></div>

        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="px-2 py-0.5 rounded-full bg-violet-500/90 text-white text-[9px] font-bold">{evento.tipo}</span>
          <DistanciaBadge km={evento.distancia} />
        </div>
        {evento.esGratuito && (
          <div className="absolute top-3 right-3">
            <span className="px-2 py-0.5 rounded-full bg-emerald-500/90 text-white text-[9px] font-bold">GRATIS</span>
          </div>
        )}
      </div>

      <div className="p-4">
        <h4 className="text-sm font-bold text-foreground-950 line-clamp-1 mb-1">{evento.titulo}</h4>
        <p className="text-[10px] text-foreground-500 mb-1.5">
          <img src={evento.radioLogoUrl} alt="" className="w-4 h-4 rounded-full object-cover inline mr-1 align-middle" />
          {evento.radioNombre}
        </p>
        <div className="flex items-center gap-3 text-[9px] text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line text-[8px]"></i>
            {fechaStr}
          </span>
          <span>{evento.hora}</span>
          <span className="flex items-center gap-1">
            <i className="ri-group-line text-[8px]"></i>
            {formatearNumero(evento.participantes)}
          </span>
        </div>
      </div>
    </Link>
  );
}

function LocutorCercanoCard({ locutor }: { locutor: LocutorCercano }) {
  return (
    <Link
      to="/radios-cercanas"
      className="group flex items-center gap-4 p-4 rounded-2xl bg-background-50 border border-background-200/70 hover:border-amber-300/60 transition-all duration-300"
    >
      <div className="relative shrink-0">
        <img src={locutor.avatarUrl} alt={locutor.nombre} className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-sm" />
        {locutor.estaEnVivo && (
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 border-2 border-white flex items-center justify-center">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-bold text-foreground-950 line-clamp-1">{locutor.nombre}</h4>
        <p className="text-[10px] text-foreground-500 mb-1">
          <img src={locutor.radioLogoUrl} alt="" className="w-3.5 h-3.5 rounded-full object-cover inline mr-1 align-middle" />
          {locutor.radioNombre} · {locutor.radioFrecuencia}
        </p>
        <div className="flex items-center gap-3 text-[9px] text-foreground-400">
          <DistanciaBadge km={locutor.distancia} />
          <span className="flex items-center gap-1">
            <i className="ri-group-line text-[8px]"></i>
            {formatearNumero(locutor.seguidores)}
          </span>
          <span className="text-foreground-500">{locutor.especialidad}</span>
        </div>
      </div>

      <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-500 transition-colors"></i>
    </Link>
  );
}

export default function RadiosCercanasSection() {
  const [activeTab, setActiveTab] = useState<'radios' | 'eventos' | 'locutores' | 'reglas'>('radios');
  const [ubicacionIdx, setUbicacionIdx] = useState(0);

  const ubicacion = ubicacionesDetectables[ubicacionIdx];
  const radiosVivo = getRadiosEnVivoCercanas(ubicacion.paisCodigo);
  const radiosTrending = getRadiosTendenciaCercanas(ubicacion.paisCodigo);
  const radiosCiudad = radiosCercanas.filter(r => r.paisCodigo === ubicacion.paisCodigo && r.ciudad === ubicacion.ciudad).sort((a, b) => a.distancia - b.distancia);
  const eventosCiudad = eventosCercanos.filter(e => e.paisCodigo === ubicacion.paisCodigo && e.ciudad === ubicacion.ciudad).sort((a, b) => a.distancia - b.distancia);
  const locutoresCiudad = locutoresCercanos.filter(l => l.paisCodigo === ubicacion.paisCodigo && l.ciudad === ubicacion.ciudad).sort((a, b) => a.distancia - b.distancia);

  const tabs = [
    { id: 'radios' as const, label: 'Radios Cerca', icon: 'ri-radio-line' },
    { id: 'eventos' as const, label: 'Eventos Cerca', icon: 'ri-calendar-event-line' },
    { id: 'locutores' as const, label: 'Locutores Cerca', icon: 'ri-user-voice-line' },
    { id: 'reglas' as const, label: 'Reglas', icon: 'ri-scales-line' },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-4">
            <i className="ri-map-pin-line"></i>
            DESCUBRÍ LO LOCAL
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Radios <span className="text-emerald-600">Cerca de Ti</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base mb-5">
            Descubrí radios, eventos y locutores que están físicamente cerca de tu ubicación. La radio local es la que mejor conoce tu comunidad.
          </p>

          {/* Location selector */}
          <div className="inline-flex items-center gap-2 bg-background-100 rounded-full p-1 border border-background-200/70">
            <span className="pl-3 text-xs text-foreground-500">📍</span>
            <select
              value={ubicacionIdx}
              onChange={(e) => setUbicacionIdx(Number(e.target.value))}
              className="bg-transparent text-xs font-semibold text-foreground-950 px-2 py-2 rounded-full outline-none cursor-pointer appearance-none pr-8"
              style={{ backgroundImage: 'none' }}
            >
              {ubicacionesDetectables.map((u, i) => (
                <option key={i} value={i}>{u.ciudad}, {u.pais}</option>
              ))}
            </select>
            <span className="pr-3 text-foreground-400 text-[10px]">
              <i className="ri-arrow-down-s-line"></i>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Radios', value: radiosCiudad.length.toString(), icon: 'ri-radio-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Eventos', value: eventosCiudad.length.toString(), icon: 'ri-calendar-event-line', color: 'text-violet-600 bg-violet-50' },
            { label: 'Locutores', value: locutoresCiudad.length.toString(), icon: 'ri-user-voice-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'En Vivo', value: radiosVivo.length.toString(), icon: 'ri-live-line', color: 'text-red-600 bg-red-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'radios' && (
          <div className="space-y-8">
            {radiosTrending.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center">
                    <i className="ri-fire-line text-rose-600 text-xs"></i>
                  </div>
                  <h3 className="text-sm font-bold text-foreground-950">En Tendencia Cerca de {ubicacion.ciudad}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {radiosTrending.slice(0, 2).map(radio => (
                    <RadioCercanaCard key={radio.id} radio={radio} size="featured" />
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-emerald-600 text-xs"></i>
                </div>
                <h3 className="text-sm font-bold text-foreground-950">Todas las Radios en {ubicacion.ciudad}</h3>
                <span className="text-xs text-foreground-400">({radiosCiudad.length})</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {radiosCiudad.map(radio => (
                  <RadioCercanaCard key={radio.id} radio={radio} />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'eventos' && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded-full bg-violet-100 flex items-center justify-center">
                <i className="ri-calendar-event-line text-violet-600 text-xs"></i>
              </div>
              <h3 className="text-sm font-bold text-foreground-950">Eventos en {ubicacion.ciudad}</h3>
              <span className="text-xs text-foreground-400">({eventosCiudad.length})</span>
            </div>
            {eventosCiudad.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {eventosCiudad.map(evento => (
                  <EventoCercanoCard key={evento.id} evento={evento} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-foreground-400">
                <i className="ri-calendar-event-line text-3xl mb-3 block"></i>
                <p className="text-sm">No hay eventos próximos en {ubicacion.ciudad}</p>
                <p className="text-xs mt-1">Probá cambiando la ubicación para descubrir eventos cercanos</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'locutores' && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-user-voice-line text-amber-600 text-xs"></i>
              </div>
              <h3 className="text-sm font-bold text-foreground-950">Locutores en {ubicacion.ciudad}</h3>
              <span className="text-xs text-foreground-400">({locutoresCiudad.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {locutoresCiudad.map(locutor => (
                <LocutorCercanoCard key={locutor.id} locutor={locutor} />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'reglas' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {reglasRadiosCercanas.map((regla) => (
              <div key={regla.id} className={`p-5 rounded-2xl bg-gradient-to-br ${regla.gradiente} bg-opacity-10 border bg-background-100`}>
                <div className="w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mb-3">
                  <i className={`${regla.icono} text-lg text-foreground-950`}></i>
                </div>
                <p className="text-sm font-bold text-foreground-950 mb-1.5">{regla.titulo}</p>
                <p className="text-xs text-foreground-600 leading-relaxed">{regla.descripcion}</p>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-10">
          <Link
            to="/radios-cercanas"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-map-pin-line"></i>
            Ver Todas las Radios Cercanas
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-8 p-5 rounded-2xl bg-emerald-50 border border-emerald-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-map-pin-user-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Siempre permitir cambiar ubicación manualmente.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Podés buscar radios en cualquier ciudad del mundo con un solo clic. La geolocalización automática es una sugerencia, nunca una imposición. El control total sobre qué radios, eventos y locutores descubrís está en tus manos.
          </p>
        </div>
      </div>
    </section>
  );
}